import { PlusOutlined } from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Row,
  Select,
  Spin,
} from 'antd';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as Back } from '../../Images/Back.svg';
import AutoOpenSelect from '../SelectComponent/AutoOpenSelect';
import BilletPlanningTable from '../../Common/BilletPlanningTable';
import { useDispatch, useSelector } from 'react-redux';
import { fetchToolByProfileId } from '../../slices/die-tool';
import { fetchDiePressList, resetCurrentDiePressPage, resetDiePressList} from '../../slices/die-press';
import { createPlanning, fetchAgeingOptionsById, fetchPriorityListByID} from '../../slices/Planning';
import dayjs from 'dayjs';;
const { Option } = Select;

function CreatePlanning() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [toolList, setToolList] = useState([]);
  const [btnLoading, setBtnLoading] = useState(false);
  const [agingResponse, setAgeingResponse] = useState(null);

  const [loading, setLoading] = useState(true);

  const [lastWeigth, setLastWeigth] = useState(null);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [dieType] = useState(null);

  const [searchDiePress, setSearchDiePress] = useState('');
  const [orderQtyCalc, setOrderQtyCalc] = useState(1);
  const [planQtyInput, setPlanQtyInput] = useState('');
  const programmaticUpdate = useRef(false);
  const [profileStats, setProfileStats] = useState({
    profileNo: 0,
    dieType: 0,
  });
  const [workOrderStats, setWorkOrderStats] = useState({
    cutLength: 0,
    PlanPcs: 0,
    totalQty: 0,
  });
  const [toolStatus, setToolStatus] = useState({
    cavity: 0,
    Wtkgmt: 0,
    press: 0,
  });

  const {
    list: diePressList,
    loading: diePressLoading,
    currentpage: diePressCurrentPage,
    hasMore: diePressHasMore,
  } = useSelector(state => state.diePress);


  useEffect(() => {
    dispatch(fetchDiePressList({ page: 1, pagesize: 5000 }));
    dispatch(resetDiePressList());
    if (id) {
      fetchData(id);
    }
  }, [id]);

  const fetchDieToolList = async id => {
    try {
      const response = await dispatch(
        fetchToolByProfileId({
          id: id,
          page: 1,
          pagesize: 10,
          search: '',
          ordering: '',
        }),
      );
      setToolList(response?.payload?.data || []);
    } catch (error) {
      console.error('Error updating work order:', error);
    }
  }

  const fetchData = async id => {
    try {
      setLoading(true);
      const response = await dispatch(fetchPriorityListByID({ id })).unwrap();
      const data = response?.results?.data[0];
     
      if (!data) {
        console.error('No data found for ID:', id);
        setLoading(false);
        return;
      }
      const profileNo = data?.profile?.die_number;
      const profileId = data?.profile?.id;
      const workOrderId = data?.workorder?.id;
      const workorderLabel = [
        data?.workorder?.order_no,
        data?.workorder?.order_date,          // if exists
        data?.workorder?.purchase_order_no,
        data?.workorder?.purchase_order_date,
        data?.customer_name,      // if exists
      ].filter(Boolean).join(' - ');
      const cutLength = data?.length_mm;
      const alloyId = data?.alloy?.id;
      const alloyLabel = data?.alloy?.alloy_code;
      const temperId = data?.temper?.id;
      const temperLabel = data?.temper?.name;
      const orderPcs = data?.total_pcs;
      const orderQty = data?.total_weight;
      form.setFieldsValue({
        profile_no: { value: profileId, label: profileNo },
        workorder: { value: workOrderId, label: workorderLabel },
        cut_length: cutLength,
        workorder_date: dayjs(data?.workorder?.order_date),
        customer_name: data?.customer_name,
        alloy: { value: alloyId, label: alloyLabel },
        temper: { value: temperId, label: temperLabel },
        color_code: data?.alloy?.color_code,
        order_pcs: orderPcs,
        order_qty: orderQty,
      });
      setWorkOrderStats(prevState => ({
        ...prevState,
        cutLength: cutLength,
      }));
      setProfileStats({
        profileNo: profileNo,
        dieType: data?.profile?.die_type,
      });
      setLastWeigth(data?.profile?.wt_kg_p_mt)
      if (profileId) {
        fetchDieToolList(profileId)
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setLoading(false);
      message.error('Failed to load data. Please try again.');
    }
  };

  const [selectedRowData, setSelectedRowData] = useState(null);
  const rowSelection = {
    type: 'radio',
    selectedRowKeys,
    onChange: (selectedKeys, selectedRows) => {
      setSelectedRowKeys(selectedKeys.slice(-1));
      setSelectedRowData(selectedRows[0] || null);
    },
    getCheckboxProps: record => ({
      disabled: false,
    }),
  };

  const handleFormChange = (changedValues, allValues) => {
    if (allValues?.cut_length && allValues?.plan_qty && allValues?.plan_pcs && allValues?.last_wt_mtr && allValues?.cavity && allValues?.tool_number) {
      const newData = generateRange(
        allValues?.cut_length,
        allValues?.plan_pcs,
        allValues?.last_wt_mtr,
        allValues?.cavity,
        allValues?.tool_number
      );
      setTableData(newData);
    }
  };

  const columns = [
    {
      title: 'BLT Size',
      dataIndex: 'bit_size',
      key: 'bit_size',
    },
    {
      title: 'BLT WT',
      dataIndex: 'bit_wt',
      key: 'bit_wt',
    },
    {
      title: 'Butt Wt',
      dataIndex: 'butt_wt',
      key: 'butt_wt',
    },
    {
      title: 'Act Wt',
      dataIndex: 'act_wt',
      key: 'act_wt',
    },
    {
      title: 'Ext Len',
      dataIndex: 'ext_len',
      key: 'ext_len',
    },
    {
      title: 'Pcs / BLT',
      dataIndex: 'pcs_bit',
      key: 'pcs_bit',
    },
    {
      title: 'Net Out',
      dataIndex: 'net_out',
      key: 'net_out',
    },
    {
      title: 'Process Recovery',
      dataIndex: 'process_recovery',
      key: 'process_recovery',
      render: (value) => {
        const num = parseFloat(value);
        let color = 'inherit';
        if (num <= 100 && num > 75) {
          color = 'green';
        } else if (num <= 75 && num > 50) {
          color = 'orange';
        } else if (num <= 50 && num > 25) {
          color = 'red';
        }
        return <span style={{ color }}>{value} %</span>;
      },
    },
    {
      title: 'Total Wastage',
      dataIndex: 'total_wastage',
      key: 'total_wastage',
    },
    {
      title: 'Total BLT',
      dataIndex: 'total_blt',
      key: 'total_blt',
    },
    {
      title: 'Total Kgs',
      dataIndex: 'total_kgs',
      key: 'total_kgs',
    },
  ];

  const onScrollDiePress = async event => {
    const target = event.target;
    if (
      !diePressLoading &&
      diePressHasMore &&
      target.scrollTop + target.offsetHeight === target.scrollHeight
    ) {
      dispatch(fetchDiePressList({ page: diePressCurrentPage, pagesize: 10 }));
    }
  };

  const [pressValue, setSelectedPressValue] = useState(null);
  const [selectedPress, setSelectedPress] = useState(null);
  const handleSelectTool = async (tool) => {

    if (tool) {
      const fetchDieCavity = tool.die_cavity;
      const fetchLastWTKGMT = tool.current_kg;
      const eligibleForPress = tool.eligible_for_press.id;
      const press = tool.eligible_for_press.name;
      setSelectedPressValue(press);
      setSelectedPress(tool?.eligible_for_press);

      form.setFieldsValue({
        cavity: fetchDieCavity,
        last_wt_mtr: fetchLastWTKGMT,
        eligible_for_press: eligibleForPress,
        tool_number: tool?.id,
      });
      setToolStatus({
        cavity: fetchDieCavity,
        press: press,
      });
    }
  };

  useEffect(() => {
    if (
      pressValue &&
      form.getFieldValue('cut_length') &&
      form.getFieldValue('plan_pcs') &&
      form.getFieldValue('last_wt_mtr') &&
      form.getFieldValue('cavity') &&
      form.getFieldValue('tool_number')
    ) {
      const newData = generateRange(
        form.getFieldValue('cut_length'),
        form.getFieldValue('plan_pcs'),
        form.getFieldValue('last_wt_mtr'),
        form.getFieldValue('cavity'),
        form.getFieldValue('tool_number')
      );
      setTableData(newData);
    }
  }, [pressValue, toolStatus, form]);

  const generateRange = (cutLength, planPcs, lastWeightKgMeter, cavity, toolNumber) => {
    // console.log(cutLength);
    // console.log(planPcs)
    // console.log(lastWeightKgMeter)
    // console.log(cavity)
    if (!pressValue || !pressValue.includes('P')) {
      console.warn('Press value not set or invalid:', pressValue);
      return [];
    }


    let range = {};
    if (pressValue.includes('P1')) {
      range = {
        start: 250,
        end: 670,
        sizes: [
          250, 300, 310, 350, 360, 390, 400, 410, 415, 430, 450, 460, 470,
          480, 490, 495, 505, 520, 530, 540, 545, 550, 560, 570, 580, 590, 610,
          620, 630, 640, 650, 660, 670,
        ],
      };
    } else if (pressValue.includes('P2')) {
      range = {
        start: 250,
        end: 570,
        sizes: [
          250, 300, 310, 350, 360, 390, 400, 410, 415, 430, 450, 460, 470,
          480, 490, 495, 505, 520, 530, 540, 545, 550, 560, 570,
        ],
      };
      if (!range.sizes) {
        console.warn('No range sizes defined for press value:', pressValue);
        return [];
      }
    }
    const safeDivide = (a, b, defaultValue = 0) => {
      if (b === 0 || isNaN(b)) return defaultValue;
      return a / b;
    };
    const safeNumber = (value, defaultValue = 0) => {
      const num = parseFloat(value);
      return isNaN(num) ? defaultValue : num;
    };
    const excelRound = (value, decimalPlaces = 2) => {
      const factor = Math.pow(10, decimalPlaces);
      return Math.round(value * factor) / factor;
    };
    const data = [];
    for (let i = 0; i < range?.sizes?.length; i++) {
      const bit_size = range.sizes[i];
      let bit_wt,
        butt_wt,
        act_wt,
        ext_len,
        star_one,
        star_two,
        pcs_bit,
        net_out,
        process_recovery,
        total_wastage,
        total_blt,
        total_blt_a,
        total_kgs;
      if (pressValue.includes('P1')) {
        bit_wt = safeNumber((bit_size * 0.034).toFixed(1));
        butt_wt = safeNumber((bit_wt * 0.04).toFixed(1));
        act_wt = safeNumber((bit_wt - butt_wt).toFixed(1));
        ext_len = safeDivide(safeDivide(act_wt, lastWeightKgMeter), cavity,);
        star_one = safeDivide(ext_len.toFixed(2), cutLength / 1000);
        star_two = parseInt(star_one);
        pcs_bit = star_two * cavity;
        net_out = pcs_bit * lastWeightKgMeter * (cutLength / 1000);
        process_recovery = safeDivide(net_out, bit_wt) * 100;
        total_wastage = (star_one.toFixed(2) - star_two) * cutLength;
        total_blt = safeDivide(planPcs, lastWeightKgMeter * pcs_bit * (cutLength / 1000),);
        total_kgs = total_blt * bit_wt;
      } else if (pressValue.includes('P2')) {
        console.log("here P2")
        bit_wt = safeNumber((bit_size * 0.024).toFixed(1));
        butt_wt = safeNumber((bit_wt * 0.04).toFixed(1));
        act_wt = safeNumber((bit_wt - butt_wt).toFixed(1));
        ext_len = act_wt / lastWeightKgMeter / cavity;;
        star_one = safeDivide(ext_len.toFixed(2), cutLength / 1000);
        star_two = parseInt(star_one);
        pcs_bit = star_two * cavity;
        net_out = pcs_bit * lastWeightKgMeter * (cutLength / 1000);
        process_recovery = safeDivide(net_out, bit_wt) * 100;
        total_wastage = (Number(star_one.toFixed(4)) - star_two) * cutLength;
        total_blt = safeDivide(planPcs, lastWeightKgMeter * pcs_bit * (cutLength / 1000));
        total_blt_a = safeDivide(Number(planPcs), lastWeightKgMeter * Number(pcs_bit) * (Number(cutLength) / 1000));;
        total_kgs = total_blt * bit_wt;
      }
      data.push({
        id: `${dieType}-${bit_size}-${i}`,
        bit_size,
        bit_wt: safeNumber(bit_wt).toFixed(1),
        butt_wt: safeNumber(butt_wt).toFixed(1),
        act_wt: safeNumber(act_wt).toFixed(1),
        ext_len: safeNumber(ext_len).toFixed(2),
        star_one: safeNumber(star_one).toFixed(2),
        star_two: safeNumber(star_two),
        pcs_bit: safeNumber(pcs_bit),
        net_out: safeNumber(net_out).toFixed(1),
        process_recovery: safeNumber(process_recovery).toFixed(2),
        total_wastage: safeNumber(total_wastage).toFixed(0),
        total_blt: safeNumber(total_blt).toFixed(0),
        total_blt_a: total_blt_a,
        total_kgs: safeNumber(total_kgs).toFixed(0),
      });
    }
    return data;
  };
  const handleSearchDiePress = value => {
    setSearchDiePress(value);
    if (value) {
      setSearchDiePress(value);
      dispatch(resetDiePressList());
      dispatch(fetchDiePressList({ page: 1, pagesize: 10, search: value }));
    } else {
      setSearchDiePress('');
      dispatch(resetDiePressList());
      dispatch(resetCurrentDiePressPage());
      dispatch(fetchDiePressList({ page: 1, pagesize: 10 }));
    }
  };
  const handleChangeDiePress = value => {
    setSearchDiePress(value);
  };
  const [agingOptions, setAgingOptions] = useState([]);

  const fetchAgingOptions = async (alloyId, temperId) => {
    if (!alloyId || !temperId) return;
    try {
      const response = await dispatch(
        fetchAgeingOptionsById({ alloyId, temperId }),
      );
      if (response?.payload?.length > 0) {
        const agingData = response.payload[0];
        setAgingOptions(agingData);
        form.setFieldsValue({ aging_value: agingData.aging_value });
        setAgeingResponse(null);
      } else {
        setAgeingResponse('No Aging Found');
        setAgingOptions(null);
        form.setFieldsValue({ aging_value: '' });
      }
    } catch (error) {
      console.error('Error fetching aging options:', error);
      setAgingOptions(null);
      form.setFieldsValue({ aging_value: '' });
    }
  };
  // useEffect(() => {
  //   fetchAgingOptions(alloyId, temperId);
  // }, [alloyId, temperId]);

  const handleConversionChange = (field, value) => {
    let currentKg = parseFloat(form.getFieldValue('last_wt_mtr')) || 0;
    let currentCutLength = parseFloat(form.getFieldValue('cut_length')) || 0;
    let orderPcs = parseFloat(form.getFieldValue('order_pcs')) || 0;
    let orderQty = parseFloat(form.getFieldValue('order_qty')) || 0;

    value = !isNaN(parseFloat(value)) ? parseFloat(value) : 0;

    if (field === 'plan_pcs') {
      if (value === orderPcs) {
        form.setFieldsValue({
          plan_qty: orderQty.toFixed(3),
        });
      } else if (currentKg > 0 && currentCutLength > 0 && value > 0) {
        const calculatedNetWeight =
          ((currentKg * currentCutLength) / 1000) * value;
        form.setFieldsValue({
          plan_qty: calculatedNetWeight.toFixed(3),
        });
      } else {
        form.setFieldsValue({
          plan_qty: '0.000',
        });
      }
    }

    if (field === 'plan_qty') {
      if (value === orderQty) {
        form.setFieldsValue({
          plan_pcs: orderPcs.toString(),
        });
      } else if (currentKg > 0 && currentCutLength > 0 && value > 0) {
        const calculatedPieces =
          value / (currentKg * (currentCutLength / 1000));
        form.setFieldsValue({
          plan_pcs: Math.round(calculatedPieces).toString(),
        });
      } else {
        form.setFieldsValue({
          plan_pcs: '0',
        });
      }
    }
  };


  const onFinish = values => {
    const payload = {
      profile_no: values?.profile_no?.value,
      plan_pcs: Number(values.plan_pcs),
      plan_qty: Number(values.plan_qty),
      workorder_detail_id: id,
      workorder: values?.workorder?.value,
      quenching_type: values?.quenching_type,
      water_pressure: values?.water_pressure,
      flow_rate: values?.flow_rate
    };
    const selectedRow = selectedRowData || {};
    payload.selected_row = selectedRow;
    payload.aging = values.aging_value || null;
    payload.die_tool = values.tool_number || null;
    dispatch(createPlanning({ values: payload }))
      .unwrap()
      .then(result => {
        if (result.success) {
          setBtnLoading(false);
          message.success('Planning Created Successfully!');
          navigate('/planning-list');
        }
      })
      .catch(error => {
        setBtnLoading(false);
        const messages = [];
        if (typeof error === 'object' && error !== null) {
          for (const key in error) {
            if (Array.isArray(error[key])) {
              messages.push(`${key}: ${error[key].join(', ')}`);
            }
          }
        }
        if (messages.length > 0) {
          message.error(messages.join('; '));
        } else if (typeof error === 'string') {
          message.error(error);
        } else {
          message.error('An unknown error occurred.');
        }
      });
  };

  if (loading) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(255,255,255,0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }
  return (
    <>
      <div
        style={{
          position: 'relative',
          padding: '24px',
          backgroundColor: '#f5f5f5',
          minHeight: '100vh',
        }}
      >
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(255,255,255,0.7)',
              zIndex: 9999,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Spin size="large" tip="Loading..." />
          </div>
        )}

        <div className="p-6 bg-gray-100 min-h-screen">
          <div className="sticyaction">
            <div className="sticky-header">
              <Row
                className="pagenamerow mb-0 "
                justify="space-between"
                align="middle"
              >
                <Col>
                  <h2 style={{ margin: 0 }}>Create Planning</h2>
                  <div className="bredcrumbwrp">
                    <Link onClick={() => navigate(-1)} className="back">
                      <Back /> BACK
                    </Link>
                    <Breadcrumb
                      items={[
                        { title: <Link to="/dashboard">Home</Link> },
                        { title: <Link to="/Priority-list">Priority List</Link> },
                        { title: 'Create Planning' },
                      ]}
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </div>
          <div className="formwrapper">
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onValuesChange={handleFormChange}
              initialValues={{
                planning_date: dayjs(),
                plan_qty: '',
                plan_pcs: '',
              }}
            >
              <Row gutter={16}>
                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item
                    label="Profile No"
                    name="profile_no"
                    rules={[
                      { required: true, message: 'Please select a profile no' },
                    ]}
                  >
                    <AutoOpenSelect
                      placeholder="Select Profile No"
                      style={{ width: '100%' }}
                      labelInValue
                      disabled
                      open={false}              // 👈 prevents dropdown
                      showSearch={false}        // 👈 no search
                      dropdownRender={() => null} // 
                    >

                    </AutoOpenSelect>
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item
                    name="workorder"
                    label="Work Order"

                  >
                    <AutoOpenSelect
                      placeholder="Order No - Order Date - PO Number - PO Date - Customer Name"
                      disabled
                      open={false}              // 👈 prevents dropdown
                      showSearch={false}        // 👈 no search
                      dropdownRender={() => null} // 
                    >
                    </AutoOpenSelect>
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item name="tool_number" label="Profile Tool">
                    <AutoOpenSelect
                      placeholder="Tool No"
                      optionFilterProp="children"
                      onChange={(value, option) => {
                        form.setFieldValue('tool_number', value); // ID stored in form
                        handleSelectTool(option.tool);            // FULL OBJECT
                      }}
                      rules={[
                        { required: true, message: 'Please Select Die Tool!' },
                      ]}
                    >
                      {toolList.map(tool => (
                        <Option key={tool?.id} value={tool?.id} tool={tool}>
                          {`${tool?.die?.die_number} - ${tool?.tool_number} - ${tool?.eligible_for_press?.name}`}
                        </Option>
                      ))}
                    </AutoOpenSelect>
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item
                    label="Cut Length"
                    name="cut_length"
                    rules={[
                      { required: true, message: 'Please enter Cut Length!' },
                    ]}
                  >
                    <Input placeholder="Cut Length" disabled />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item
                    label="Work Order Date"
                    name="workorder_date"
                    rules={[
                      {
                        required: false,
                        message: 'Please select Work Order Date.',
                      },
                    ]}
                  >
                    <DatePicker
                      format="DD-MM-YYYY"
                      disabled
                      className="w-full"
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item label="Customer Name" name="customer_name">
                    <Input placeholder="Enter Customer Name" disabled />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item label="Alloy" name="alloy">
                    <AutoOpenSelect
                      placeholder="Select Alloy"
                      labelInValue
                      disabled
                      open={false}
                      dropdownRender={() => null}
                    >
                    </AutoOpenSelect>
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item label="Temper" name="temper">
                    <AutoOpenSelect
                      placeholder="Select Temper"
                      labelInValue
                      disabled
                      open={false}
                      dropdownRender={() => null}
                    >

                    </AutoOpenSelect>
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item label="Color Code/Grade" name="color_code">
                    <Input placeholder="Enter Color Code/Grade" disabled />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item
                    label="Order PCS"
                    name="order_pcs"
                    rules={[
                      {
                        required: false,
                        message: 'Please enter Order PCS.',
                      },
                      {
                        pattern: /^[0-9]*\.?[0-9]+$/,
                        message: 'Only numbers and decimals are allowed!',
                      },
                    ]}
                  >
                    <Input
                      type="primary"
                      disabled
                      placeholder="Enter Order PCS"
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item
                    label="Order QTY"
                    name="order_qty"
                    rules={[
                      {
                        pattern: /^[0-9]*\.?[0-9]+$/,
                        message: 'Only numbers and decimals are allowed!',
                      },
                    ]}
                  >
                    <Input
                      type="primary"
                      disabled
                      placeholder="Enter Order QTY"
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item
                    label="Planning Date"
                    name="planning_date"
                    rules={[
                      { required: true, message: 'Please select Planning Date.' },
                    ]}
                  >
                    <DatePicker
                      format="DD-MM-YYYY"
                      className="w-full"
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item
                    label="Plan PCS"
                    name="plan_pcs"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter Plan PCS',
                      },
                    ]}
                  >
                    <Input
                      type="text"
                      placeholder="Enter Plan PCS"
                      maxLength={10}
                      autoComplete="off"
                      onChange={e => {
                        const value = Number(e.target.value);
                        setTimeout(
                          () => handleConversionChange('plan_pcs', value),
                          0,
                        );
                        setOrderQtyCalc(value);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item
                    label="Plan QTY"
                    name="plan_qty"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter plan qty',
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter Plan QTY"
                      value={planQtyInput}
                      onChange={e => {
                        const rawValue = e.target.value;
                        setPlanQtyInput(rawValue);
                        if (programmaticUpdate.current) {
                          programmaticUpdate.current = false;
                          return;
                        }

                        if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                          const value = parseFloat(rawValue);
                          if (!isNaN(value)) {
                            setOrderQtyCalc(value);
                            setTimeout(
                              () => handleConversionChange('plan_qty', value),
                              0,
                            );
                            if (workOrderStats.cutLength && toolStatus.cavity) {
                              const newData = generateRange(
                                workOrderStats.cutLength,
                                workOrderStats.totalQty,
                                toolStatus.cavity,
                                toolStatus.Wtkgmt,
                                toolStatus.press,
                              );
                              setTableData(newData);
                            }
                          }
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item
                    name="quenching_type"
                    label="Quenching Type"
                    rules={[{ required: true, message: 'Please select quenching type' }]}
                  >
                    <Select placeholder="Select Quenching Type">
                      <Select.Option value="AIR">Air Quench</Select.Option>
                      <Select.Option value="WATER_SPRAY">Water Spray Quench</Select.Option>
                      <Select.Option value="MIST">Mist / Fog Quench</Select.Option>
                      <Select.Option value="WATER_DIP">Water Dip Quench</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Form.Item
                  shouldUpdate={(prev, curr) => prev.quenching_type !== curr.quenching_type}
                  noStyle
                >
                  {({ getFieldValue }) => {
                    const quenchType = getFieldValue('quenching_type');

                    return ['WATER_SPRAY', 'MIST', 'WATER_DIP'].includes(quenchType) ? (
                      <>
                        <Col xs={24} sm={12} xl={8} xxl={4}>
                          <Form.Item
                            name="water_pressure"
                            label="Water Pressure (bar)"
                            rules={[{ required: true, message: 'Enter water pressure' }]}
                          >
                            <Input type="number" min={0} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} xl={8} xxl={4}>
                          <Form.Item
                            name="flow_rate"
                            label="Flow Rate (LPM)"
                            rules={[{ required: true, message: 'Enter flow rate' }]}
                          >
                            <Input type="number" min={0} />
                          </Form.Item>
                        </Col>
                      </>
                    ) : null;
                  }}
                </Form.Item>

                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item
                    label="Aging"
                    name="aging_value"
                    rules={[{ required: true, message: 'Please select Aging.' }]}
                  >
                    <Input placeholder="Enter Aging" />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item label="Cavity" name="cavity">
                    <Input placeholder="Enter Cavity" disabled />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item label="Last WT/MTR" name="last_wt_mtr">
                    <Input placeholder="Enter Last WT MTR" />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={6} xxl={4}>
                  <Form.Item name="eligible_for_press" label="Press">
                    <AutoOpenSelect
                      disabled
                      placeholder="Select Press"
                      loading={diePressLoading}
                      onPopupScroll={onScrollDiePress}
                      notFoundContent={
                        diePressLoading ? 'Loading...' : 'No options'
                      }
                      value={searchDiePress}
                      showSearch={true}
                      onSearch={handleSearchDiePress}
                      onChange={handleChangeDiePress}
                      allowClear
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      dropdownRender={menu => <>{menu}</>}
                    >
                      {diePressList.map(el => (
                        <Option key={el.id} value={el.id}>
                          {el.name}
                        </Option>
                      ))}
                      {diePressLoading && <Option>Loading...</Option>}
                    </AutoOpenSelect>
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <div className="widwrp" style={{ backgroundColor: '#f0fff0' }}>
                    <div className="widtitle"></div>
                    <div className="vidvaluerow">
                      <div>
                        <label style={{ fontSize: '1.3rem' }}>Profile No</label>
                        <span
                          style={{
                            fontSize: '1.5rem',
                            wordBreak: 'break-all',
                            fontWeight: 'bold',
                          }}
                        >
                          {' '}
                          {profileStats.profileNo}
                        </span>
                      </div>
                      <div>
                        <label style={{ fontSize: '1.3rem' }}>Cavity</label>
                        <span
                          style={{
                            fontSize: '1.5rem',
                            wordBreak: 'break-all',
                            fontWeight: 'bold',
                          }}
                        >
                          {' '}
                          {toolStatus.cavity}{' '}
                        </span>
                      </div>
                      <div>
                        <label style={{ fontSize: '1.3rem' }}>Last WT/MTR</label>
                        <span
                          style={{
                            fontSize: '1.5rem',
                            wordBreak: 'break-all',
                            fontWeight: 'bold',
                          }}
                        >
                          {' '}
                          {lastWeigth}
                        </span>
                      </div>
                      <div>
                        <label style={{ fontSize: '1.3rem' }}>
                          CUT LENGTH(mm)
                        </label>
                        <span
                          style={{
                            fontSize: '1.5rem',
                            wordBreak: 'break-all',
                            fontWeight: 'bold',
                          }}
                        >
                          {' '}
                          {workOrderStats.cutLength}
                        </span>
                      </div>
                      <div>
                        <label style={{ fontSize: '1.3rem' }}> Billet Size</label>
                        <span
                          style={{
                            fontSize: '1.5rem',
                            wordBreak: 'break-all',
                            fontWeight: 'bold',
                          }}
                        >
                          {' '}
                          {selectedRowData?.bltSize}
                        </span>
                      </div>
                      <div>
                        <label style={{ fontSize: '1.3rem' }}>Total Billet</label>
                        <span
                          style={{
                            fontSize: '1.5rem',
                            wordBreak: 'break-all',
                            fontWeight: 'bold',
                          }}
                        >
                          {' '}
                          {selectedRowData?.totalBillets}
                        </span>
                      </div>
                      <div>
                        <label style={{ fontSize: '1.3rem' }}>
                          Total Wastage
                        </label>
                        <span
                          style={{
                            fontSize: '1.5rem',
                            wordBreak: 'break-all',
                            fontWeight: 'bold',
                          }}
                        >
                          {' '}
                          {selectedRowData?.totalWastage}
                        </span>
                      </div>
                      <div>
                        <label style={{ fontSize: '1.3rem' }}>No. of Pcs</label>
                        <span
                          style={{
                            fontSize: '1.5rem',
                            wordBreak: 'break-all',
                            fontWeight: 'bold',
                          }}
                        >
                          {' '}
                          {selectedRowData?.pcsPerBlt}
                        </span>
                      </div>
                      <div>
                        <label style={{ fontSize: '1.3rem' }}>
                          Process Recovery(%)
                        </label>
                        <span
                          style={{
                            fontSize: '1.5rem',
                            wordBreak: 'break-all',
                            fontWeight: 'bold',
                          }}
                        >
                          {' '}
                          {selectedRowData?.recovery}
                        </span>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              <BilletPlanningTable
                lastWtPerMtr={form.getFieldValue("last_wt_mtr")}
                cutLengthMm={form.getFieldValue("cut_length")}
                cavity={form.getFieldValue("cavity")}
                planPcs={form.getFieldValue("plan_pcs")}
                press={selectedPress}
                onRowSelect={(row) => {
                  setSelectedRowData(row)
                }}
              />

              <div className="flex justify-end mt-5">
                <Form.Item>
                  <Button
                    disabled={selectedRowData === null}
                    type="primary"
                    htmlType="submit"
                    icon={<PlusOutlined />}
                  >
                    Add Planning
                  </Button>
                </Form.Item>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
}
export default CreatePlanning;